import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Primitives";
import { toast } from "sonner";
import { Plus, UploadSimple } from "@phosphor-icons/react";
import * as XLSX from "xlsx";

const DEMO_ACCOUNT_EMAILS = new Set(["admin@lms.com", "teacher@lms.com", "student@lms.com"]);
const CLASS_GROUPS = ["Class 9", "Class 10", "Class 11", "Class 12", "B.Com", "B.Sc", "B.CA"];
const SECTION_OPTIONS = ["A", "B", "C", "D","E","F","G","H","I","J"];
const CUSTOM_SECTION_VALUE = "__custom__";

/** Normalize Excel column headers so "CLASS", "Class ", "class_group" match. */
function excelHeaderNorm(k) {
  return String(k ?? "")
    .replace(/^\uFEFF/, "")
    .replace(/\u00a0/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/_/g, " ")
    .toLowerCase();
}

/** One row keyed by normalized header (first non-empty wins per key). */
function rowByNormalizedHeaders(row) {
  const out = {};
  const normCell = (v) => String(v ?? "").trim();
  for (const [k, v] of Object.entries(row)) {
    const nk = excelHeaderNorm(k);
    if (!nk) continue;
    if (out[nk] === undefined || normCell(out[nk]) === "") {
      out[nk] = v;
    }
  }
  return out;
}

/** Read a field trying several normalized alias names. */
function takeNorm(normRow, aliases) {
  for (const a of aliases) {
    const v = normRow[a];
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s !== "") return s;
  }
  return "";
}

/** Map pasted / numeric class cells to canonical CLASS_GROUPS when possible. */
function coerceImportedClassGroup(raw) {
  if (raw === undefined || raw === null || raw === "") return "";
  const t = typeof raw === "number" && Number.isFinite(raw) ? String(Math.trunc(raw)) : String(raw).trim();
  if (!t) return "";
  if (CLASS_GROUPS.includes(t)) return t;
  const collapsed = t.replace(/\u00a0/g, " ").replace(/\s+/g, " ");
  const lower = collapsed.toLowerCase();
  if (CLASS_GROUPS.includes(collapsed)) return collapsed;
  // "Class10", "class-10", "CLASS 11"
  const cn = lower.match(/^class\s*[-_]?\s*(\d+)/);
  if (cn) {
    const cand = `Class ${cn[1]}`;
    if (CLASS_GROUPS.includes(cand)) return cand;
  }
  // Lone grade number "10" → Class 10
  const num = lower.match(/^(\d{1,2})$/);
  if (num) {
    const cand = `Class ${num[1]}`;
    if (CLASS_GROUPS.includes(cand)) return cand;
  }
  // B stream variants
  const bsc = lower.replace(/\s+/g, "");
  if (bsc === "b.com" || bsc === "bcom") return "B.Com";
  if (bsc === "b.sc" || bsc === "bsc") return "B.Sc";
  if (bsc === "b.ca" || bsc === "bca") return "B.CA";
  return collapsed;
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("");
  const [editingRoles, setEditingRoles] = useState({});
  const [editingProfiles, setEditingProfiles] = useState({});
  const [editingEnabled, setEditingEnabled] = useState({});
  const [savingUserId, setSavingUserId] = useState("");
  const [savingProfileUserId, setSavingProfileUserId] = useState("");
  const [deletingUserId, setDeletingUserId] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [importingUsers, setImportingUsers] = useState(false);
  const excelRef = useRef(null);
  const [newUser, setNewUser] = useState({
    full_name: "",
    email: "",
    password: "",
    role: "student",
    phone: "",
    class_group: "Class 10",
    section: "A",
  });

  const load = useCallback(() => {
    api.get(`/users${role ? `?role=${role}` : ""}`).then((r) => {
      setUsers(r.data);
      const nextRoles = {};
      const nextProfiles = {};
      r.data.forEach((u) => {
        nextRoles[u.id] = u.role;
        nextProfiles[u.id] = {
          full_name: u.full_name || "",
          email: u.email || "",
          class_group: u.class_group || "Class 10",
          section: u.section || "A",
        };
      });
      setEditingRoles(nextRoles);
      setEditingProfiles(nextProfiles);
    });
  }, [role]);

  useEffect(() => {
    load();
  }, [load]);

  const startEditing = (userId) => {
    setEditingEnabled((prev) => ({ ...prev, [userId]: true }));
  };

  const cancelEditing = (userId) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;
    setEditingRoles((prev) => ({ ...prev, [userId]: user.role }));
    setEditingProfiles((prev) => ({
      ...prev,
      [userId]: {
        full_name: user.full_name || "",
        email: user.email || "",
        class_group: user.class_group || "Class 10",
        section: user.section || "A",
      },
    }));
    setEditingEnabled((prev) => ({ ...prev, [userId]: false }));
  };

  const changeRole = async (userId) => {
    const newRole = editingRoles[userId];
    const user = users.find((u) => u.id === userId);
    if (!user || !newRole || newRole === user.role) return;
    setSavingUserId(userId);
    try {
      const { data } = await api.patch(`/users/${userId}/role`, { role: newRole });
      setUsers((prev) => prev.map((u) => (u.id === userId ? data : u)));
      setEditingRoles((prev) => ({ ...prev, [userId]: data.role }));
      setEditingEnabled((prev) => ({ ...prev, [userId]: false }));
      toast.success(`Updated ${data.full_name} to ${data.role}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update role");
    } finally {
      setSavingUserId("");
    }
  };

  const saveProfile = async (userId) => {
    const user = users.find((u) => u.id === userId);
    const profile = editingProfiles[userId];
    if (!user || !profile) return;
    setSavingProfileUserId(userId);
    try {
      const payload = {
        full_name: profile.full_name,
        email: profile.email,
        class_group: user.role === "student" ? profile.class_group : null,
        section: user.role === "student" ? profile.section : null,
      };
      const { data } = await api.patch(`/users/${userId}`, payload);
      setUsers((prev) => prev.map((u) => (u.id === userId ? data : u)));
      setEditingProfiles((prev) => ({
        ...prev,
        [userId]: {
          full_name: data.full_name || "",
          email: data.email || "",
          class_group: data.class_group || "Class 10",
          section: data.section || "A",
        },
      }));
      setEditingEnabled((prev) => ({ ...prev, [userId]: false }));
      toast.success("User details updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update user");
    } finally {
      setSavingProfileUserId("");
    }
  };

  const removeUser = async (userId) => {
    if (!window.confirm("Delete this user account?")) return;
    setDeletingUserId(userId);
    try {
      await api.delete(`/users/${userId}`);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      toast.success("User deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeletingUserId("");
    }
  };

  const resetNewUser = () => {
    setNewUser({
      full_name: "",
      email: "",
      password: "",
      role: "student",
      phone: "",
      class_group: "Class 10",
      section: "A",
    });
  };

  const createManualUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    try {
      const payload = {
        full_name: newUser.full_name.trim(),
        email: newUser.email.trim(),
        password: newUser.password,
        role: newUser.role,
        phone: newUser.phone.trim() || null,
        class_group: newUser.role === "student" ? newUser.class_group : null,
        section: newUser.role === "student" ? newUser.section : null,
      };
      await api.post("/auth/register", payload);
      toast.success("User created");
      setManualOpen(false);
      resetNewUser();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create user");
    } finally {
      setCreatingUser(false);
    }
  };

  const downloadUsersTemplate = () => {
    const rows = [
      ["FULL NAME", "EMAIL", "PASSWORD", "ROLE", "PHONE (OPTIONAL)", "CLASS", "SECTION"],
      ["Student One", "student1@lms.com", "student123", "student", "+910000000010", "Class 10", "A"],
      ["Teacher One", "teacher1@lms.com", "teacher123", "teacher", "+910000000011", "", ""],
      ["Admin One", "admin2@lms.com", "secure123", "admin", "+910000000012", "", ""],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, "users_import_template.xlsx");
  };

  const importUsersFromExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportingUsers(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
      if (!rows.length) {
        toast.error("Excel file is empty");
        return;
      }

      let created = 0;
      const errors = [];
      const normRowAliases = {
        full_name: ["full name", "fullname", "name"],
        email: ["email", "e mail", "e-mail", "email id", "mail id"],
        password: ["password", "passwd", "pass"],
        role: ["role"],
        phone: ["phone (optional)", "phone optional", "phone", "mobile", "contact"],
        class_group: ["class", "class group", "classgroup", "class/stream", "stream", "grade", "std"],
        section: ["section", "sec", "division"],
      };
      for (let i = 0; i < rows.length; i += 1) {
        const nr = rowByNormalizedHeaders(rows[i]);
        const line = i + 2;
        const roleValue = (takeNorm(nr, normRowAliases.role) || "student").toLowerCase();
        const roleSafe = ["admin", "teacher", "student"].includes(roleValue) ? roleValue : "student";
        const rawClass = takeNorm(nr, normRowAliases.class_group);
        const classCoerced = roleSafe === "student" ? coerceImportedClassGroup(rawClass) : "";
        const payload = {
          full_name: takeNorm(nr, normRowAliases.full_name),
          email: takeNorm(nr, normRowAliases.email),
          password: takeNorm(nr, normRowAliases.password),
          role: roleSafe,
          phone: takeNorm(nr, normRowAliases.phone) || null,
          class_group: roleSafe === "student" ? (classCoerced || "Class 10") : null,
          section:
            roleSafe === "student" ? ((takeNorm(nr, normRowAliases.section) || "A").trim().toUpperCase()) : null,
        };
        if (!payload.full_name || !payload.email || !payload.password) {
          errors.push(`Row ${line}: full_name, email, password are required`);
          continue;
        }
        if (payload.role === "student" && !SECTION_OPTIONS.includes(payload.section)) {
          payload.section = "A";
        }
        try {
          await api.post("/auth/register", payload);
          created += 1;
        } catch (err) {
          errors.push(`Row ${line}: ${err?.response?.data?.detail || "failed"}`);
        }
      }

      if (created > 0) {
        toast.success(`Imported ${created} user(s)`);
        load();
      }
      if (errors.length > 0) {
        const preview = errors.slice(0, 3).join(" | ");
        toast.error(`${errors.length} row(s) failed: ${preview}`);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to import users");
    } finally {
      setImportingUsers(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Users" title="All accounts"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setManualOpen(true)}
              className="btn-primary text-xs px-3 py-2 inline-flex items-center gap-1.5"
              data-testid="add-user-manual"
            >
              <Plus size={14} /> Add user manually
            </button>
            <button
              type="button"
              onClick={() => excelRef.current?.click()}
              disabled={importingUsers}
              className="btn-secondary text-xs px-3 py-2 inline-flex items-center gap-1.5"
              data-testid="add-user-excel"
            >
              <UploadSimple size={14} /> {importingUsers ? "Importing..." : "Add using Excel"}
            </button>
            <button
              type="button"
              onClick={downloadUsersTemplate}
              className="btn-ghost text-xs px-3 py-2 border border-stone"
              data-testid="download-users-template"
            >
              Template
            </button>
            <input
              ref={excelRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={importUsersFromExcel}
              data-testid="users-excel-file"
            />
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="filter-role"
              className="bg-ivory border border-stone px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-terracotta">
              <option value="">All roles</option>
              <option value="admin">Admin</option>
              <option value="teacher">Teacher</option>
              <option value="student">Student</option>
            </select>
          </div>
        } />
      {users.length === 0 ? <EmptyState title="No users" /> : (
        <div className="card-academic overflow-x-auto">
          <table className="w-full min-w-[960px] text-sm">
            <thead className="bg-sand"><tr>
              <th className="p-4 overline text-left min-w-[11rem]">Name</th>
              <th className="p-4 overline text-left min-w-[14rem]">Email</th>
              <th className="p-4 overline text-left">Role</th>
              <th className="p-4 overline text-left">Change role</th>
              <th className="p-4 overline text-left">Class</th>
              <th className="p-4 overline text-left">Section</th>
              <th className="p-4 overline text-left">Joined</th>
              <th className="p-4 overline text-left">Actions</th>
            </tr></thead>
            <tbody>
              {users.map((u) => {
                const isDemo = DEMO_ACCOUNT_EMAILS.has((u.email || "").toLowerCase());
                const profile = editingProfiles[u.id] || { full_name: "", email: "", class_group: "Class 10", section: "A" };
                const canEdit = !isDemo && !!editingEnabled[u.id];
                const sectionSelectValue = SECTION_OPTIONS.includes(profile.section) ? profile.section : CUSTOM_SECTION_VALUE;
                return (
                <tr key={u.id} className="border-b border-stone hover:bg-bone/50">
                  <td className="p-4 min-w-[11rem] align-top">
                    <input
                      value={profile.full_name}
                      title={(profile.full_name || "").trim() || undefined}
                      onChange={(e) => setEditingProfiles((prev) => ({
                        ...prev,
                        [u.id]: { ...profile, full_name: e.target.value },
                      }))}
                      disabled={!canEdit}
                      className="w-full max-w-full bg-ivory border border-stone px-3 py-2 disabled:opacity-60"
                    />
                  </td>
                  <td className="p-4 min-w-[14rem] align-top">
                    <input
                      value={profile.email}
                      title={(profile.email || "").trim() || undefined}
                      onChange={(e) => setEditingProfiles((prev) => ({
                        ...prev,
                        [u.id]: { ...profile, email: e.target.value },
                      }))}
                      disabled={!canEdit}
                      className="w-full max-w-full bg-ivory border border-stone px-3 py-2 font-mono text-xs disabled:opacity-60"
                    />
                  </td>
                  <td className="p-4 capitalize">{u.role}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <select
                        value={editingRoles[u.id] || u.role}
                        onChange={(e) => setEditingRoles((prev) => ({ ...prev, [u.id]: e.target.value }))}
                        disabled={!canEdit}
                        className="bg-ivory border border-stone px-3 py-2 focus:outline-none focus:ring-2 focus:ring-terracotta"
                      >
                        <option value="admin">Admin</option>
                        <option value="teacher">Teacher</option>
                        <option value="student">Student</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => changeRole(u.id)}
                        disabled={!canEdit || savingUserId === u.id || (editingRoles[u.id] || u.role) === u.role}
                        className="border border-stone px-3 py-2 text-xs uppercase tracking-wide disabled:opacity-50"
                      >
                        {savingUserId === u.id ? "Saving..." : "Update"}
                      </button>
                      {isDemo && (
                        <span className="text-xs text-mossy uppercase tracking-wide">Locked</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    {u.role === "student" ? (
                      <select
                        value={profile.class_group}
                        onChange={(e) => setEditingProfiles((prev) => ({
                          ...prev,
                          [u.id]: { ...profile, class_group: e.target.value },
                        }))}
                        disabled={!canEdit}
                        className="bg-ivory border border-stone px-3 py-2 disabled:opacity-60"
                      >
                        {CLASS_GROUPS.map((cg) => <option key={cg} value={cg}>{cg}</option>)}
                      </select>
                    ) : (
                      <span className="text-mossy">—</span>
                    )}
                  </td>
                  <td className="p-4">
                    {u.role === "student" ? (
                      <div className="space-y-2">
                        <select
                          value={sectionSelectValue}
                          onChange={(e) => setEditingProfiles((prev) => ({
                            ...prev,
                            [u.id]: { ...profile, section: e.target.value === CUSTOM_SECTION_VALUE ? "" : e.target.value },
                          }))}
                          disabled={!canEdit}
                          className="bg-ivory border border-stone px-3 py-2 disabled:opacity-60"
                        >
                          {SECTION_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                          <option value={CUSTOM_SECTION_VALUE}>Custom</option>
                        </select>
                        {sectionSelectValue === CUSTOM_SECTION_VALUE && (
                          <input
                            value={profile.section}
                            onChange={(e) => setEditingProfiles((prev) => ({
                              ...prev,
                              [u.id]: { ...profile, section: e.target.value },
                            }))}
                            disabled={!canEdit}
                            placeholder="Enter custom section"
                            className="w-full bg-ivory border border-stone px-3 py-2 disabled:opacity-60"
                          />
                        )}
                      </div>
                    ) : (
                      <span className="text-mossy">—</span>
                    )}
                  </td>
                  <td className="p-4 font-mono text-xs">{u.created_at?.slice(0, 10)}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => startEditing(u.id)}
                        disabled={isDemo || !!editingEnabled[u.id]}
                        className="border border-stone p-2 disabled:opacity-50"
                        title="Edit user"
                        aria-label="Edit user"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => saveProfile(u.id)}
                        disabled={!canEdit || savingProfileUserId === u.id}
                        className="border border-stone px-3 py-2 text-xs uppercase tracking-wide disabled:opacity-50"
                      >
                        {savingProfileUserId === u.id ? "Saving..." : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelEditing(u.id)}
                        disabled={!editingEnabled[u.id]}
                        className="border border-stone px-3 py-2 text-xs uppercase tracking-wide disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => removeUser(u.id)}
                        disabled={isDemo || deletingUserId === u.id}
                        className="border border-red-300 text-red-700 px-3 py-2 text-xs uppercase tracking-wide disabled:opacity-50"
                      >
                        {deletingUserId === u.id ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {manualOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 pt-8 overflow-y-auto" onClick={(e) => {
          if (e.target === e.currentTarget) setManualOpen(false);
        }}>
          <form onSubmit={createManualUser} className="bg-ivory border border-stone w-full max-w-xl p-6 shadow-xl">
            <h3 className="font-serif text-3xl text-forest mb-1">Add user manually</h3>
            <p className="text-xs text-mossy mb-4">Only admins can create users.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="overline block mb-2">Full name</label>
                <input required value={newUser.full_name} onChange={(e) => setNewUser((p) => ({ ...p, full_name: e.target.value }))}
                  className="w-full bg-bone border border-stone px-4 py-3" />
              </div>
              <div>
                <label className="overline block mb-2">Email</label>
                <input type="email" required value={newUser.email} onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
                  className="w-full bg-bone border border-stone px-4 py-3" />
              </div>
              <div>
                <label className="overline block mb-2">Password</label>
                <input type="text" required value={newUser.password} onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
                  className="w-full bg-bone border border-stone px-4 py-3" />
              </div>
              <div>
                <label className="overline block mb-2">Role</label>
                <select value={newUser.role} onChange={(e) => setNewUser((p) => ({ ...p, role: e.target.value }))}
                  className="w-full bg-bone border border-stone px-4 py-3">
                  <option value="admin">Admin</option>
                  <option value="teacher">Teacher</option>
                  <option value="student">Student</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Phone (optional)</label>
                <input value={newUser.phone} onChange={(e) => setNewUser((p) => ({ ...p, phone: e.target.value }))}
                  className="w-full bg-bone border border-stone px-4 py-3" />
              </div>
              {newUser.role === "student" && (
                <>
                  <div>
                    <label className="overline block mb-2">Class</label>
                    <select value={newUser.class_group} onChange={(e) => setNewUser((p) => ({ ...p, class_group: e.target.value }))}
                      className="w-full bg-bone border border-stone px-4 py-3">
                      {CLASS_GROUPS.map((cg) => <option key={cg} value={cg}>{cg}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline block mb-2">Section</label>
                    <select
                      value={SECTION_OPTIONS.includes(newUser.section) ? newUser.section : CUSTOM_SECTION_VALUE}
                      onChange={(e) => setNewUser((p) => ({
                        ...p,
                        section: e.target.value === CUSTOM_SECTION_VALUE ? "" : e.target.value,
                      }))}
                      className="w-full bg-bone border border-stone px-4 py-3"
                    >
                      {SECTION_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      <option value={CUSTOM_SECTION_VALUE}>Custom</option>
                    </select>
                    {!SECTION_OPTIONS.includes(newUser.section) && (
                      <input
                        value={newUser.section}
                        onChange={(e) => setNewUser((p) => ({ ...p, section: e.target.value }))}
                        placeholder="Enter custom section"
                        className="mt-2 w-full bg-bone border border-stone px-4 py-3"
                      />
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setManualOpen(false)} className="btn-ghost border border-stone px-4 py-2">Cancel</button>
              <button disabled={creatingUser} className="btn-primary px-4 py-2">{creatingUser ? "Creating..." : "Create user"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
